import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

const LOGO = `${process.env.PUBLIC_URL}/1.png`;
const REDIRECT_URL = "https://app.insightos.co.uk/logout";
const STAGE_INTERVAL_MS = 20000;

const STAGES = [
  "Payment made",
  "Populating account",
  "Setting up workspace",
  "Finalizing access",
];

function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
}

function formatDate(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
}

export default function TrialPaymentSuccess() {
  const { stripesubscriptionid } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageIndex, setStageIndex] = useState(0);

  const activeStage = useMemo(() => STAGES[Math.min(stageIndex, STAGES.length - 1)], [stageIndex]);
  const displayLastPaymentAmount = Number(company?.LastPaymentAmount || 0);
  const displayVatAmount = Number((displayLastPaymentAmount * 0.2).toFixed(2));

  useEffect(() => {
    let cancelled = false;

    const loadCompany = async () => {
      try {
        const response = await fetch("https://api.insightos.co.uk/api/company", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error("Failed to load company");
        }

        const data = await response.json();
        if (!cancelled) {
          setCompany(data?.data?.[0] || null);
        }
      } catch (_err) {
        if (!cancelled) {
          setCompany(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCompany();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setStageIndex((prev) => Math.min(prev + 1, STAGES.length - 1));
    }, STAGE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const handleLogout = () => {
    window.location.href = REDIRECT_URL;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-100 px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl rounded-3xl border border-white/35 bg-white/85 p-8 shadow-[0_16px_40px_rgba(15,23,42,0.16)] backdrop-blur-xl">
        <div className="mb-6 flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="InsightOS" className="h-12 w-12 rounded-lg object-cover" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Trial Payment Invoice</h1>
              <p className="text-sm text-slate-500">Welcome to InsightOS. We are just setting up your account.</p>
            </div>
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            {activeStage}
          </div>
        </div>

        <div className="mb-8 flex items-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-lg font-semibold text-emerald-700">Payment Received</p>
            <p className="text-sm text-emerald-800">Your trial conversion is complete. Final account setup is now in progress.</p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Company Name</p>
            <p className="mt-1 text-xl font-bold text-slate-900">{loading ? "..." : company?.CompanyName || "N/A"}</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Users</p>
            <p className="mt-1 text-xl font-bold text-slate-900">{loading ? "..." : company?.Users ?? "N/A"}</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Last Payment Amount</p>
            <p className="mt-1 text-xl font-bold text-indigo-700">£{loading ? "..." : formatMoney(company?.LastPaymentAmount)}</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">VAT Amount (20%)</p>
            <p className="mt-1 text-xl font-bold text-emerald-700">£{loading ? "..." : formatMoney(displayVatAmount)}</p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Current Period End</p>
            <p className="mt-1 text-xl font-bold text-slate-900">{loading ? "..." : formatDate(company?.CurrentPeriodEnd)}</p>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
          <p>
            <span className="font-semibold">Subscription Reference:</span> {company?.StripeSubscriptionId || stripesubscriptionid || "N/A"}
          </p>
          <p className="mt-1 text-slate-500">
            When you are ready, continue with the button below.
          </p>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleLogout}
            className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            Continue to Logout
          </button>
        </div>
      </div>
    </div>
  );
}
